import { createContext, useContext, useState, ReactNode } from 'react';
import { apiCall } from '../utils/api';

interface QuoteLineItem {
  id: string;
  description: string;
  price: number;
  quantity: number;
  discount?: number;
}

interface Quote {
  _id?: string;
  title: string;
  customer: string;
  totalPrice: number;
  lineItems: QuoteLineItem[];
  workflowId?: string;
  approved?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface WorkflowStage {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  order: number;
  connectedPersona: string;
  timeCreated: Date;
  timeCompleted?: Date;
}

interface Workflow {
  _id?: string;
  userId: string;
  quoteId: string;
  title: string;
  stages: WorkflowStage[];
  createdAt?: string;
  updatedAt?: string;
}

interface QuoteContextType {
  // Quote state
  currentQuote: Quote | null;
  setCurrentQuote: (quote: Quote | null) => void;
  updateQuoteField: (field: keyof Quote, value: any) => void;
  updateLineItem: (index: number, field: keyof QuoteLineItem, value: any) => void;
  addLineItem: () => void;
  removeLineItem: (index: number) => void;
  
  // Workflow state
  currentWorkflow: Workflow | null;
  setCurrentWorkflow: (workflow: Workflow | null) => void;
  updateWorkflowStage: (stageId: string, updates: Partial<WorkflowStage>) => void;
  reorderWorkflowStages: (newStages: WorkflowStage[]) => void;
  
  // Operations
  generateQuote: (prompt: string, responseId?: string) => Promise<{response: Quote, responseId: string}>;
  saveQuote: () => Promise<any>;
  deleteQuote: () => Promise<void>;
  loadWorkflow: (workflowId: string) => Promise<void>;
  saveWorkflow: () => Promise<void>;
  
  // Loading states
  isGenerating: boolean;
  isSaving: boolean;
  isLoadingWorkflow: boolean;
  
  // Response ID for follow-ups
  responseId: string | null;
  setResponseId: (id: string | null) => void;
}

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};

interface QuoteProviderProps {
  children: ReactNode;
  user: any;
}

export const QuoteProvider = ({ children, user }: QuoteProviderProps) => {
  const [currentQuote, setCurrentQuote] = useState<Quote | null>(null);
  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(null);

  console.log(`RESPONSE ID: ${responseId}`);

  const updateQuoteField = (field: keyof Quote, value: any) => {
    if (!currentQuote) return;
    console.log(`Updating quote field ${field} to:`, value);
    setCurrentQuote({ ...currentQuote, [field]: value });
  };

  const updateLineItem = (index: number, field: keyof QuoteLineItem, value: any) => {
    if (!currentQuote) return;
    console.log(`Updating line item ${index} field ${field} to:`, value);
    const newLineItems = [...currentQuote.lineItems];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    
    // Calculate new total price
    const newTotalPrice = newLineItems.reduce((total, item) => {
      const itemTotal = (item.price || 0) * (item.quantity || 1) * (1 - (item.discount || 0) / 100);
      return total + itemTotal;
    }, 0);
    
    setCurrentQuote({ 
      ...currentQuote, 
      lineItems: newLineItems,
      totalPrice: newTotalPrice
    });
  };

  const addLineItem = () => {
    if (!currentQuote) return;
    const newLineItem: QuoteLineItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      description: '',
      price: 0,
      quantity: 1,
      discount: 0
    };
    const newLineItems = [...currentQuote.lineItems, newLineItem];
    
    // Recalculate total price
    const newTotalPrice = newLineItems.reduce((total, item) => {
      const itemTotal = (item.price || 0) * (item.quantity || 1) * (1 - (item.discount || 0) / 100);
      return total + itemTotal;
    }, 0);
    
    setCurrentQuote({
      ...currentQuote,
      lineItems: newLineItems,
      totalPrice: newTotalPrice
    });
  };

  const removeLineItem = (index: number) => {
    if (!currentQuote) return;
    const newLineItems = currentQuote.lineItems.filter((_, i) => i !== index);
    
    // Recalculate total price
    const newTotalPrice = newLineItems.reduce((total, item) => {
      const itemTotal = (item.price || 0) * (item.quantity || 1) * (1 - (item.discount || 0) / 100);
      return total + itemTotal;
    }, 0);
    
    setCurrentQuote({ 
      ...currentQuote, 
      lineItems: newLineItems,
      totalPrice: newTotalPrice
    });
  };

  const updateWorkflowStage = (stageId: string, updates: Partial<WorkflowStage>) => {
    if (!currentWorkflow || !currentQuote) return;
    
    const newStages = currentWorkflow.stages.map(stage => {
      if (stage.id === stageId) {
        const updatedStage = { ...stage, ...updates };
        
        // If marking as completed, set timeCompleted
        if (updates.completed === true && !stage.completed) {
          updatedStage.timeCompleted = new Date();
        }
        
        // If marking as incomplete, remove timeCompleted
        if (updates.completed === false && stage.completed) {
          updatedStage.timeCompleted = undefined;
        }
        
        return updatedStage;
      }
      return stage;
    });
    
    // Check if all user-actionable stages are completed (exclude order 0)
    const allStagesCompleted = newStages.filter(s => s.order > 0).every(stage => stage.completed);
    
    // Update workflow
    setCurrentWorkflow({ ...currentWorkflow, stages: newStages });
    
    // Update quote approval status based on workflow completion
    if (currentQuote.approved !== allStagesCompleted) {
      setCurrentQuote({ ...currentQuote, approved: allStagesCompleted });
    }
  };

  const reorderWorkflowStages = (newStages: WorkflowStage[]) => {
    if (!currentWorkflow || !currentQuote) return;
    
    // Update order numbers based on new position
    const reorderedStages = newStages.map((stage, index) => ({
      ...stage,
      order: index + 1
    }));
    
    // Check if all user-actionable stages are completed after reordering (exclude order 0)
    const allStagesCompleted = reorderedStages.filter(s => s.order > 0).every(stage => stage.completed);
    
    setCurrentWorkflow({ ...currentWorkflow, stages: reorderedStages });
    
    // Update quote approval status
    if (currentQuote.approved !== allStagesCompleted) {
      setCurrentQuote({ ...currentQuote, approved: allStagesCompleted });
    }
  };

  const generateQuote = async (prompt: string, prevResponseId?: string) => {
    setIsGenerating(true);
    try {
      const requestBody: any = { 
        userPrompt: prompt,
        previousResponseId: prevResponseId || null
      };
      
      console.log('Sending request body:', requestBody);
      console.log('Previous response ID being sent:', prevResponseId);

      const response = await fetch('/quotes/prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': user.id
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      if (result.success) {
        // Ensure all line items have IDs
        const quoteWithIds = {
          ...result.data.response,
          lineItems: result.data.response.lineItems?.map((item: any, index: number) => ({
            ...item,
            id: item.id || `item_${Date.now()}_${index}_${Math.random().toString(36).substring(2, 9)}`
          })) || []
        };
        
        setCurrentQuote(quoteWithIds);
        setResponseId(result.data.responseId);
        return result.data;
      } else {
        throw new Error(result.message);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const saveQuote = async () => {
    if (!currentQuote) return;
    
    console.log('Saving quote:', currentQuote);
    console.log('Current workflow before save:', currentWorkflow);
    
    setIsSaving(true);
    try {
      const response = await fetch('/quotes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': user.id
        },
        body: JSON.stringify(currentQuote)
      });

      const result = await response.json();
      if (result.success) {
        console.log('Save response:', result.data);
        
        // Update the current quote with saved data, but preserve workflow state
        const updatedQuote = {
          ...result.data,
          // Preserve any workflow data that might not be in the response
          workflowId: result.data.workflowId || currentQuote.workflowId
        };
        
        console.log('Updated quote after save:', updatedQuote);
        console.log('Workflow should still be:', currentWorkflow);
        
        setCurrentQuote(updatedQuote);
        return result.data;
      } else {
        throw new Error(result.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const loadWorkflow = async (workflowId: string) => {
    setIsLoadingWorkflow(true);
    try {
      const response = await fetch(`/workflows/${workflowId}`);
      const result = await response.json();
      if (result.success) {
        setCurrentWorkflow(result.data);
      } else {
        throw new Error(result.message);
      }
    } finally {
      setIsLoadingWorkflow(false);
    }
  };

  const deleteQuote = async () => {
    if (!currentQuote || !currentQuote._id) return;
    
    setIsSaving(true);
    try {
      const response = await apiCall(`/quotes/${currentQuote._id}`, {
        method: 'DELETE',
        headers: {
          'user-id': user.id
        }
      });

      const result = await response.json();
      if (result.success) {
        // Clear current quote and workflow from context
        setCurrentQuote(null);
        setCurrentWorkflow(null);
        return;
      } else {
        throw new Error(result.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveWorkflow = async () => {
    if (!currentWorkflow) return;
    
    console.log('Saving workflow:', currentWorkflow);
    
    setIsSaving(true);
    try {
      const response = await fetch(`/workflows/${currentWorkflow._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'user-id': user.id
        },
        body: JSON.stringify({
          title: currentWorkflow.title,
          stages: currentWorkflow.stages
        })
      });

      const result = await response.json();
      console.log('Workflow save response:', result);
      
      if (result.success) {
        // Update workflow but don't replace the entire object to preserve state
        const updatedWorkflow = {
          ...currentWorkflow,
          ...result.data,
          stages: result.data.stages || currentWorkflow.stages
        };
        console.log('Updated workflow after save:', updatedWorkflow);
        setCurrentWorkflow(updatedWorkflow);
        return result.data;
      } else {
        throw new Error(result.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const value: QuoteContextType = {
    currentQuote,
    setCurrentQuote,
    updateQuoteField,
    updateLineItem,
    addLineItem,
    removeLineItem,
    currentWorkflow,
    setCurrentWorkflow,
    updateWorkflowStage,
    reorderWorkflowStages,
    generateQuote,
    saveQuote,
    deleteQuote,
    loadWorkflow,
    saveWorkflow,
    isGenerating,
    isSaving,
    isLoadingWorkflow,
    responseId,
    setResponseId
  };

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
};